import { createColumnHelper } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';

import Table from '../common/Table';
import { IPaginationTable } from '../common/interfaces/IPaginationTable';

import {
	calculateStatus,
	formatHaggleEventDate,
	formatPrice,
} from '@/components/utils/utils';
import {
	IHaggleEventMonthlySalesReport,
	IHaggleEventSalesReport,
} from '@/interfaces/api/haggle-event/IHaggleEventSalesReport';

const HaggleMonthlySalesReportTable = ({
	haggle,
	paginationOption,
}: {
	haggle: IHaggleEventMonthlySalesReport;
	paginationOption: IPaginationTable;
}) => {
	const navigate = useNavigate();
	const columnHelper = createColumnHelper<IHaggleEventSalesReport>();

	const columns = [
		columnHelper.accessor('id', {
			cell: (info) => <div className="w-[10px]">{info.getValue()}</div>,
			header: () => <div className="w-[10px]">ID</div>,
		}),
		{
			id: 'status',
			cell: (info: { row: { original: unknown } }) => {
				const haggleEvent = info.row.original as IHaggleEventSalesReport;
				return (
					<div className="w-[50px]">
						{calculateStatus(haggleEvent.endDate, haggleEvent.startDate)}
					</div>
				);
			},
			header: () => <div className="w-[50px]">STATUS</div>,
		},
		columnHelper.accessor('startDate', {
			cell: (info) => (
				<div className="w-[170px] text-center">
					{formatHaggleEventDate(
						info.row.original.startDate,
						info.row.original.endDate,
					)}
				</div>
			),
			header: () => <div className="w-[170px] text-center">DATE</div>,
		}),
		columnHelper.accessor('acceptableMargin', {
			cell: (info) => (
				<div className="w-[140px] text-center">{`${info.getValue()}%`}</div>
			),
			header: () => <div className="w-[140px] text-center">MAX. DISCOUNT</div>,
		}),
		columnHelper.accessor('agreements', {
			cell: (info) => {
				const agreementsReached = info.getValue()?.length;
				const totalDeals = info
					.getValue()
					?.filter((agreement) => agreement.isSold).length;
				return (
					<div className="w-[120px] text-center">{`${totalDeals}/${agreementsReached}`}</div>
				);
			},
			header: () => <div className="w-[120px] text-center">SALES/DEALS</div>,
		}),
		columnHelper.accessor('totalSalesMade', {
			cell: (info) => {
				const totalSalesMade = info.getValue()?.toFixed(2) ?? 'N/A';

				return (
					<div className="w-[150px] text-center">{`${formatPrice(
						totalSalesMade,
					)}`}</div>
				);
			},
			header: () => <div className="w-[150px] text-center">TOTAL SALES</div>,
		}),
		columnHelper.accessor('totalProfit', {
			cell: (info) => {
				const totalProfit = info.getValue()?.toFixed(2) ?? 'N/A';

				return (
					<div className="w-[220px] text-center">{`${formatPrice(
						totalProfit,
					)}`}</div>
				);
			},
			header: () => (
				<div className="w-[220px] text-center">ADDED PROFIT MARGIN</div>
			),
		}),
		columnHelper.accessor('productIds', {
			cell: (info) => (
				<div className="w-[100px] text-center">{`${
					info.getValue().length
				}`}</div>
			),
			header: () => <div className="w-[100px]">PRODUCTS</div>,
		}),
	];

	return (
		<div
			className="flex flex-col items-center border-t border-gray-300 w-full bg-grey-0 border rounded-md"
			data-test="store-haggles-table"
		>
			<Table
				collection={{
					data: haggle.data.events || [],
					pageNumber: haggle.pageNumber,
					pageSize: haggle.pageSize,
					pageCount: haggle.pageCount,
					itemCount: haggle.itemCount,
				}}
				paginationSection={paginationOption}
				columns={columns}
				handleSelectRow={(row) => {
					navigate(`/user/dashboard/haggle-events/${row.id}`);
				}}
			/>
		</div>
	);
};

export default HaggleMonthlySalesReportTable;
